import { useEffect, useState } from "react";
import { ModeSelect, type GameMode, type GameOptions } from "./ModeSelect";
import { GameScreen } from "./GameScreen";
import { OnlineLobby, type OnlineSession } from "./OnlineLobby";
import { attachOnce } from "./online-attach";
import { TutorialMode } from "./TutorialMode";
import { theme, FONT_RUNE } from "./theme";
import { clearGameUrl, gameIdFromUrl } from "./route";
import { clearSession, loadSession, loadSessionFromServer } from "./session";
import { useGame } from "./store";
import { connect } from "../net/client";
import type { GameState } from "../engine";

interface PlayConfig {
  mode: GameMode;
  options: GameOptions;
  online?: OnlineSession;
  /** Restored from /g/<id> on initial load. */
  restoredState?: GameState;
  restoredSessionId?: string;
}

type Screen =
  | { kind: "menu" }
  | { kind: "lobby"; options: GameOptions }
  | { kind: "tutorial" }
  | { kind: "play"; config: PlayConfig }
  | { kind: "reconnecting"; sessionId: string; message: string };

export function App() {
  const [screen, setScreen] = useState<Screen>({ kind: "menu" });
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const endSession = useGame((s) => s.endSession);

  // On mount: if the URL is /g/<id>, try to restore the game.
  //   1. localStorage first — instant; works for any mode, signed-in or not (device-local).
  //   2. If miss and the user is signed in: fall back to the D1 API and replay the move log.
  //      This is the cross-device path (start on phone → continue on web).
  // Local/bot games jump straight to GameScreen with the restored engine state. Online games
  // reconnect via the worker (rejoinToken from localStorage); cross-device online resume isn't
  // wired up yet — the rejoinToken is device-local.
  useEffect(() => {
    const urlId = gameIdFromUrl();
    if (!urlId) return;

    const restoreToPlay = (saved: { mode: "local" | "bot"; victoryConfig: import("../engine").VictoryConfig; humanSide: import("../engine").Side; state: import("../engine").GameState; sessionId: string }) => {
      setScreen({
        kind: "play",
        config: {
          mode: saved.mode,
          options: { victoryConfig: saved.victoryConfig, humanSide: saved.humanSide },
          restoredState: saved.state,
          restoredSessionId: saved.sessionId,
        },
      });
    };

    const saved = loadSession(urlId);
    if (saved) {
      if (saved.mode === "local" || saved.mode === "bot") {
        restoreToPlay({ ...saved, mode: saved.mode });
        return;
      }
      if (saved.mode === "online" && saved.online) {
        const { code, rejoinToken } = saved.online;
        setScreen({ kind: "reconnecting", sessionId: saved.sessionId, message: `Reconnecting to ${code}…` });
        const client = connect(code, { rejoinToken });
        attachOnce(
          client,
          code,
          (session) => {
            setScreen({
              kind: "play",
              config: {
                mode: "online",
                options: { victoryConfig: saved.victoryConfig, humanSide: session.mySide },
                online: session,
                restoredSessionId: saved.sessionId,
              },
            });
          },
          (msg) => {
            setRestoreError(msg ?? "couldn't reconnect to your online game");
            clearSession(saved.sessionId);
            clearGameUrl();
            setScreen({ kind: "menu" });
          },
        );
        return;
      }
      clearGameUrl();
      return;
    }

    // Cross-device fallback: localStorage miss. Try the D1 API.
    setScreen({ kind: "reconnecting", sessionId: urlId, message: "Loading game…" });
    void loadSessionFromServer(urlId).then((result) => {
      if (result.kind === "ok" && (result.data.mode === "bot" || result.data.mode === "local")) {
        restoreToPlay({ ...result.data, mode: result.data.mode });
        return;
      }
      if (result.kind === "online-unsupported") {
        setRestoreError("That game is an online match — open it from the device you started it on, or rejoin via its lobby code.");
      }
      // not-found or unsupported — drop the URL and show menu.
      clearGameUrl();
      setScreen({ kind: "menu" });
    });
  }, []);

  const exitToMenu = () => {
    if (screen.kind === "play") {
      if (screen.config.online) {
        try { screen.config.online.client.close(); } catch { /* noop */ }
      }
      // endSession() reads the live store.sessionId, which is the upgraded D1 gameRecordId
      // for signed-in bot/online games (not the original restoredSessionId from the URL).
      endSession();
    }
    clearGameUrl();
    setScreen({ kind: "menu" });
  };

  if (screen.kind === "reconnecting") {
    return (
      <div style={overlayStyle}>
        <div className="rune" style={{ color: theme.accentSoft, fontSize: 12, letterSpacing: 3 }}>RECONNECTING</div>
        <div style={{ color: theme.parchment, fontSize: 16, marginTop: 12 }}>{screen.message}</div>
        <button
          onClick={() => {
            clearSession(screen.sessionId);
            clearGameUrl();
            setScreen({ kind: "menu" });
          }}
          style={cancelButton}
        >
          Cancel
        </button>
      </div>
    );
  }

  if (screen.kind === "tutorial") {
    return <TutorialMode onExit={exitToMenu} />;
  }

  if (screen.kind === "lobby") {
    return (
      <OnlineLobby
        victoryConfig={screen.options.victoryConfig}
        onCancel={exitToMenu}
        onConnected={(session) => {
          setScreen({
            kind: "play",
            config: {
              mode: "online",
              options: { ...screen.options, humanSide: session.mySide },
              online: session,
            },
          });
        }}
      />
    );
  }

  if (screen.kind === "menu") {
    return (
      <>
        {restoreError && (
          <div style={errorBanner}>{restoreError}</div>
        )}
        <ModeSelect
          onWatchTutorial={() => setScreen({ kind: "tutorial" })}
          onStart={(mode, options) => {
            if (mode === "online") {
              setScreen({ kind: "lobby", options });
              return;
            }
            setScreen({ kind: "play", config: { mode, options } });
          }}
        />
      </>
    );
  }

  return (
    <GameScreen
      mode={screen.config.mode}
      options={screen.config.options}
      online={screen.config.online}
      restoredState={screen.config.restoredState}
      restoredSessionId={screen.config.restoredSessionId}
      onExit={exitToMenu}
    />
  );
}

const overlayStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  background: theme.bg,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
  fontFamily: FONT_RUNE,
};

const cancelButton: React.CSSProperties = {
  marginTop: 28,
  padding: "8px 14px",
  background: theme.boardEdge,
  color: theme.parchment,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 6,
  fontFamily: FONT_RUNE,
  fontSize: 14,
  cursor: "pointer",
};

const errorBanner: React.CSSProperties = {
  position: "fixed",
  top: 12,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 100,
  padding: "8px 14px",
  background: theme.bgElev,
  color: theme.captureFlash,
  border: `1px solid ${theme.captureFlash}`,
  borderRadius: 6,
  fontFamily: FONT_RUNE,
  fontSize: 13,
};
